/**
 * NativeBridge — MXR-005 · 2026-08-05 · extended for RC7 M1-A (2026-08-10)
 *
 * Bootstraps the Capacitor-only integrations. Renders nothing;
 * mounted once at the AuthProvider level inside App.js. Guarded by
 * `isNativePlatform()` so the plain-web bundle skips the whole
 * initialization path.
 *
 * Responsibilities:
 *   · Deep-link handler for `cruisecommand://…` URLs — the OAuth
 *     callback lands here and gets normalized into an in-app route.
 *   · Hardware back button (Android) — matches expected native
 *     behavior: pop the current modal or route rather than
 *     exit the app on first press.
 *   · RC7 · M1-A · Connectivity subscription bootstrap — primes the
 *     shared `connectivity` module so downstream consumers receive an
 *     initial status without each having to trigger `getStatus()`.
 *
 * Reserved for later (documented in ADR-MXR-005 §6):
 *   · Push Notifications registration (plugin installed, not
 *     activated in M1-A per PO §9).
 *   · Foreground state changes (App / AppState).
 *   · Keyboard event coordination with the mobile bottom tab bar.
 */
import { useEffect } from "react";
import { isNativePlatform } from "@/lib/platform";
import { subscribeConnectivity } from "@/lib/mobile/connectivity";
import { parseDeepLink, createDeepLinkDeliveryGuard } from "@/lib/mobile/deepLinkParser";

// Module-scoped so a remount (StrictMode, provider re-key) never re-delivers
// an OAuth handle that was already handed to AuthCallback. Cleared on logout.
const deliveryGuard = createDeepLinkDeliveryGuard();

/**
 * Applies a native OAuth deep link to the running app: the URL is parsed,
 * de-duplicated, and its params are written to `window.location.hash`.
 * Assigning the hash fires `hashchange`, which AuthContext observes to flip
 * `googleCallbackPending` and mount <AuthCallback>. Returns true when the
 * link was applied.
 */
export function applyOAuthDeepLink(rawUrl) {
  const parsed = parseDeepLink(rawUrl || "");
  if (!parsed.valid) return false;                 // malformed / unknown / unsafe URL — no-op
  if (!deliveryGuard.accept(parsed.params)) return false; // no OAuth handle or duplicate delivery
  window.location.hash = parsed.hash;
  // The provider leg ran in the in-app system browser; dismiss it now that
  // the callback has landed (best-effort, no-op if nothing is open).
  import("@/lib/mobile/nativeBrowser").then((m) => m.closeSystemBrowser()).catch(() => {});
  return true;
}

export default function NativeBridge() {
  useEffect(() => {
    if (!isNativePlatform()) return;

    let appListener;
    let backListener;
    let connectivityUnsub;
    const onLogout = () => deliveryGuard.reset();
    window.addEventListener("cc:logout", onLogout);

    (async () => {
      try {
        const { App } = await import("@capacitor/app");

        // Deep-link handler (warm start). When the OAuth provider redirects
        // into `cruisecommand://auth-callback#session_id=...`, Capacitor
        // fires `appUrlOpen` on the already-running WebView.
        appListener = await App.addListener("appUrlOpen", (event) => {
          applyOAuthDeepLink(event?.url || "");
        });

        // Cold start: the intent that launched the process may have been
        // consumed before this listener existed. Replay it through the same
        // guarded path (the guard drops it if appUrlOpen already delivered it).
        try {
          const launch = await App.getLaunchUrl?.();
          if (launch?.url) applyOAuthDeepLink(launch.url);
        } catch { /* plugin without getLaunchUrl — ignore */ }

        // Hardware back button (Android). Pop the current hash if
        // it isn't the root, otherwise let the platform handle it
        // (which by default minimizes the app).
        backListener = await App.addListener("backButton", ({ canGoBack }) => {
          if (canGoBack && window.history.length > 1) {
            window.history.back();
          } else {
            App.exitApp?.();
          }
        });

        // RC7 · M1-A · §3 · Prime connectivity monitoring. The
        // subscribeConnectivity module is idempotent — this call
        // ensures at least one listener is attached at boot so
        // native connectivity events are recorded from the moment
        // the app becomes interactive. Individual UI consumers can
        // still call useConnectivity() to observe status.
        connectivityUnsub = subscribeConnectivity(() => { /* observer */ });
      } catch (_e) {
        /* Capacitor plugins unavailable — safe to no-op */
      }
    })();

    return () => {
      window.removeEventListener("cc:logout", onLogout);
      try { appListener?.remove?.(); } catch { /* ignore */ }
      try { backListener?.remove?.(); } catch { /* ignore */ }
      try { connectivityUnsub?.(); } catch { /* ignore */ }
    };
  }, []);

  return null;
}
